<template>
  <!-- While redirecting a bound task to its object screen, avoid rendering
       any object. -->
  <div v-if="redirectedToObject" class="task-route-redirecting" />
  <Obj v-else :id="id" :value="value">
    <Task
      v-if="value"
      ref="component"
      :value="value"
      :embedded-mode="embeddedMode"
    />
  </Obj>
</template>

<script lang="ts">
import { isEqual, map } from "lodash-es";
import { defineComponent, onMounted, onUpdated, toRefs, watch } from "vue";
import { useRoute } from "vue-router";

import type { FullTask, Task } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import Component from "@/components/task/Task.vue";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import {
  isBoundTaskId,
  isWellFormedObjectId,
  isWellFormedTaskId,
} from "@/utils/id";

import Obj from "./DefaultObject.vue";
import { useObject } from "./object";
import { RouteNames } from "./types";

export default defineComponent({
  components: {
    Obj,
    Task: Component,
  },
  props: {
    id: { type: String, required: true },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const { id } = toRefs(props);
    const controller = inject.controller();
    const router = inject.router();
    const route = useRoute();
    const {
      initializeComponentFocus,
      installRouteCallbacks,
      release,
      setObjectValue,
      ...object
    } = useObject(route, (value: FullTask) =>
      map(
        ["completed", "notes", "title"] satisfies Array<keyof Task>,
        (prop: keyof Task) =>
          watch(
            () => value![prop],
            (v, previous) => {
              if (!isEqual(v, previous))
                controller.task.update(
                  value.id,
                  {
                    [prop]: v,
                  },
                  "app",
                  null
                );
            }
          )
      )
    );
    installRouteCallbacks();
    onMounted(initializeComponentFocus);
    onUpdated(initializeComponentFocus);
    const releaser = useReleaser();
    releaser.defer(release);

    // A bound task — a graph row (`object:…`) surfaced as a generalized
    // task — always displays as its custom object screen (`Object.vue`
    // renders the completion checkbox); the task screen only hosts native
    // tasks. Programmatic navigations are normalized by `navigateToObject`;
    // this guards direct URL entry (deep links, bookmarks). Only claimed
    // when the redirect actually fires: a malformed bound id must fall
    // through to the loader below and render as not-found, not sit on the
    // blank redirect placeholder forever.
    let redirectedToObject = false;
    if (isBoundTaskId(id.value) && isWellFormedObjectId(id.value)) {
      const { workspace_id, table_id, row_id } =
        await controller.graph.object.id.parse(id.value);
      if (workspace_id && table_id && row_id) {
        redirectedToObject = true;
        await router.replace({
          name: RouteNames.Object,
          params: {
            workspaceId: workspace_id,
            tableId: table_id,
            rowId: row_id,
          },
        });
      }
    }

    const task = await releaser.add(
      bindAsync(id, async (id) => {
        // The id comes straight from the URL: `task.get` fatals on malformed
        // ids at the RPC schema boundary, so a bad deep link (e.g. several
        // ids joined with spaces, FRONTEND-F4Q) must render as not-found
        // instead of reaching the RPC. Only native ids load here — valid
        // bound ids redirect to the object screen above, and malformed ones
        // yield null (not-found) — so the screen handles `FullTask` values
        // only.
        const t =
          id && isWellFormedTaskId(id)
            ? await controller.task.get(id)
            : releasableRef(null);
        return t.map((t) =>
          t
            ? ({
                ...t,
                completed: t.completed ?? null,
              } satisfies FullTask)
            : t
        );
      })
    );
    setObjectValue(task);
    return { ...object, redirectedToObject };
  },
});
</script>
